import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

/**
 * Self-contained guided product tour. Mirrors the legacy intro.js walkthrough
 * (`.old/resources/views/home.blade.php` + `structure/report.blade.php`):
 * a dimmed overlay spotlights one element at a time while a tooltip card with
 * a progress bar and Back/Continue buttons steps the user through the UI.
 *
 * No external dependency — positioning is computed from `getBoundingClientRect`
 * so it stays in sync with the React-rendered DOM. Each step targets an element
 * by CSS selector (legacy used `.introstepN` classes; here we use `data-tour`).
 */
export interface TourStep {
  /** CSS selector for the element to spotlight (e.g. `[data-tour="step1"]`). */
  selector: string;
  /** Tooltip body. May contain simple HTML (matches legacy `<b>` usage). */
  intro: string;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 6;
const TOOLTIP_WIDTH = 300;

export function ProductTour({
  steps,
  run,
  onClose,
}: {
  steps: TourStep[];
  run: boolean;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  const step = steps[index];

  const measure = useCallback(() => {
    if (!run || !step) return;
    const el = document.querySelector(step.selector) as HTMLElement | null;
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    // Hidden/unrendered targets (e.g. responsive `display:none`) report a
    // zero-size rect — treat them as "no target" so the step centers instead
    // of spotlighting the top-left corner.
    if (r.width === 0 && r.height === 0) {
      setRect(null);
      return;
    }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [run, step]);

  // Reset to first step whenever the tour (re)starts.
  useEffect(() => {
    if (run) setIndex(0);
  }, [run]);

  // Bring the target into view before measuring.
  useLayoutEffect(() => {
    if (!run || !step) return;
    const el = document.querySelector(step.selector) as HTMLElement | null;
    if (!el) {
      measure();
      return;
    }

    el.scrollIntoView({ behavior: 'auto', block: 'center' });
    const frame = window.requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [run, step, measure]);

  useEffect(() => {
    if (!run) return;
    const onChange = () => measure();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [run, measure]);

  // Keyboard navigation (Esc to exit, arrows to step).
  useEffect(() => {
    if (!run) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, steps.length - 1));
      else if (e.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [run, steps.length, onClose]);

  if (!run || !step) return null;

  const isFirst = index === 0;
  const isLast = index === steps.length - 1;
  const progress = ((index + 1) / steps.length) * 100;

  // Spotlight box-shadow technique: a huge spread shadow dims the whole page
  // while leaving the highlighted rect cut out (matches intro.js helperLayer).
  const highlight: React.CSSProperties = rect
    ? {
        position: 'fixed',
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
        borderRadius: 4,
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
        transition: 'all 0.2s ease-out',
        zIndex: 1090,
        pointerEvents: 'none',
      }
    : {};

  // Place the tooltip below the target when there is room, otherwise above.
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  let tipTop: number;
  let placeBelow = true;
  if (rect) {
    const below = rect.top + rect.height + PADDING + 12;
    if (below + 160 > vh && rect.top - 12 > 160) {
      placeBelow = false;
      tipTop = rect.top - PADDING - 12;
    } else {
      tipTop = below;
    }
  } else {
    tipTop = vh / 2 - 80;
  }
  let tipLeft = rect
    ? Math.min(Math.max(rect.left, 12), vw - TOOLTIP_WIDTH - 12)
    : vw / 2 - TOOLTIP_WIDTH / 2;
  if (tipLeft < 12) tipLeft = 12;

  const tooltipStyle: React.CSSProperties = {
    position: 'fixed',
    top: tipTop,
    left: tipLeft,
    transform: placeBelow ? undefined : 'translateY(-100%)',
    width: TOOLTIP_WIDTH,
    maxWidth: 'calc(100vw - 24px)',
    backgroundColor: '#fff',
    borderRadius: 6,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
    padding: '16px 18px 14px',
    zIndex: 1091,
    fontSize: 14,
    lineHeight: 1.45,
    color: '#333',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Guided tour"
      style={{ position: 'fixed', inset: 0, zIndex: 1089 }}
    >
      {/* Backdrop: dims and blocks interaction. Without a target, dim fully. */}
      {!rect && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        />
      )}
      {rect && <div style={highlight} />}

      <div style={tooltipStyle}>
        <button
          type="button"
          aria-label="Close tour"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 8,
            right: 10,
            border: 'none',
            background: 'none',
            fontSize: 18,
            lineHeight: 1,
            cursor: 'pointer',
            color: '#666',
          }}
        >
          &times;
        </button>

        <div
          style={{ marginBottom: 12, paddingRight: 12 }}
          // Legacy tour copy includes <b> emphasis; render it faithfully.
          dangerouslySetInnerHTML={{ __html: step.intro }}
        />

        <div
          style={{
            height: 4,
            borderRadius: 2,
            backgroundColor: '#e2e2e2',
            marginBottom: 12,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progress}%`,
              backgroundColor: '#0d6efd',
              transition: 'width 0.2s ease-out',
            }}
          />
        </div>

        <div className="d-flex justify-content-between align-items-center">
          <button
            type="button"
            className="btn btn-sm btn-light"
            disabled={isFirst}
            onClick={() => setIndex((i) => Math.max(i - 1, 0))}
          >
            Back
          </button>
          {isLast ? (
            <button type="button" className="btn btn-sm btn-success" onClick={onClose}>
              Done
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-success"
              onClick={() => setIndex((i) => Math.min(i + 1, steps.length - 1))}
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
